import { Divider, makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React from 'react';
import { Download, ExternalLink } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { usePrefixTranslation } from '../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { enumToIcon, enumToStatusType } from '../../../types/common';
import { downloadFileDialog, getContentUrl } from '../../../util/file';
import { Button, IconButton, Radio, StatusIcon } from '../../global';
import { ProjectCard } from '../../project/common/ProjectCard';
import { RiskMatrix } from '../common/RiskMatrix';
import overviewGeckoLogo from './OverviewGeckoLogo.svg';
import overviewHardDrive from './OverviewHardDrive.svg';
import overviewSleepyCalendar from './OverviewSleepyCalendar.svg';

const useOverviewClasses = makeStyles({
    root: {
        display: 'flex',
        gap: 30,
        flex: 1,
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        '&:first-child': {
            flex: 1,
        },
    },
});

export const Overview = () => {
    const classes = useOverviewClasses();
    const { asset, plan } = useTypedSelector(state => ({ asset: state.asset, plan: state.plan }));
    const fetching = asset.fetching || plan.fetching;

    if (!asset.asset || !plan.plan) return <span>{'#asset or plan not in store'}</span>;

    return (
        <div className={classes.root}>
            <div className={classes.column}>
                <ActiveProject />
                <AssetActivities />
            </div>
            <div className={classes.column}>
                {fetching ? (
                    <Skeleton height={50} />
                ) : (
                    <RiskMatrix
                        likelihood={plan.plan.risk_assessment?.likelihood_risk}
                        consequence={plan.plan.risk_assessment?.consequence_risk}
                        collapse
                        collapsed
                    />
                )}
                <AssetImage />
                <LinkCard
                    label='Gecko Portal'
                    icon={<img src={overviewGeckoLogo} alt='gecko portal' />}
                    href='https://portal.geckorobotics.com/'
                />
                <LinkCard
                    label='Equipment Drive'
                    icon={<img src={overviewHardDrive} alt='equipment drive' />}
                    href='https://portal.geckorobotics.com/'
                />
            </div>
        </div>
    );
};

const useActiveProjectClasses = makeStyles({
    rootActive: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        color: theme.colors.darkBlue[5],
        ...theme.typography.headings.h3,
        '& > div': {
            background: theme.colors.neutral[4],
        },
    },
    rootSchedule: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        color: theme.colors.neutral[2],
        ...theme.typography.content.text,
        '& > div': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 10,
        },
    },
});

/**
 * Render the active project of the asset in the store.
 */
const ActiveProject = () => {
    const classes = useActiveProjectClasses();
    const { t } = usePrefixTranslation('asset', { asset: 'drawer.Overview' });
    const navigate = useNavigate();
    const dispatch = useTypedDispatch();
    const { asset, plan, activeProject } = useTypedSelector(state => ({
        asset: state.asset,
        plan: state.plan,
        activeProject: state.planProjects.active,
    }));

    React.useEffect(() => void (plan.plan && dispatch(actions.planProjects.active())), [plan.plan]);

    if (!asset.asset || !plan.plan) return <span>{'#asset or plan not in store'}</span>;

    if (activeProject.fetching)
        return (
            <div className={classes.rootActive}>
                <Skeleton width='25%' />
                <Skeleton variant='rect' height={100} />
            </div>
        );

    return activeProject.project ? (
        <div className={classes.rootActive}>
            <span>{t('active-project')}</span>
            <ProjectCard name={asset.asset.name} project={activeProject.project} />
        </div>
    ) : (
        <div className={classes.rootSchedule}>
            <div>
                <span>{t('no-project')}</span>
                <Button variant='contained' onClick={() => navigate('/projects')}>
                    {t('schedule')}
                </Button>
            </div>
            <img src={overviewSleepyCalendar} width={140} height={96} alt='no project' />
        </div>
    );
};

const useAssetActivitiesClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        color: theme.colors.darkBlue[5],
        ...theme.typography.headings.h3,
    },
    activities: {
        display: 'grid',
        gridAutoRows: 45,
        gridTemplateColumns: '140px 40px 1fr',
        alignItems: 'center',
        gap: 8,
        ...theme.typography.content.caption.default,
    },
    date: {
        color: theme.colors.neutral[2],
    },
    radio: {
        '&.Mui-checked': {
            color: `${theme.colors.darkBlue[5]} !important`,
        },
        '&.Mui-disabled': {
            color: `${theme.colors.neutral[6]} !important`,
        },
    },
    status: {
        color: theme.colors.neutral[1],
        textTransform: 'capitalize',
    },
});
const AssetActivities = () => {
    const classes = useAssetActivitiesClasses();
    const { t } = usePrefixTranslation(['asset', 'common'], { asset: 'drawer.Overview' });
    const dispatch = useTypedDispatch();
    const { asset, assetLogs } = useTypedSelector(state => ({
        asset: state.asset,
        assetLogs: state.assetLogs,
    }));

    React.useEffect(() => void (asset.asset && dispatch(actions.assetLogs.get())), [asset.asset]);

    if (!asset.asset) return <span>{'#asset not in store'}</span>;

    if (assetLogs.fetching)
        return (
            <div className={classes.root}>
                <Skeleton width='25%' />
                <div className={classes.activities}>
                    {[...Array(5)].map((_, i) => (
                        <React.Fragment key={i}>
                            <Skeleton width={`${50 + 50 * Math.random()}%`} />
                            <span />
                            <Skeleton width={`${20 + 50 * Math.random()}%`} />
                        </React.Fragment>
                    ))}
                </div>
            </div>
        );

    return (
        <div className={classes.root}>
            <span>{t('asset-activities')}</span>
            <Divider />
            <div className={classes.activities}>
                {[...assetLogs.logs].reverse().map((log, i) => (
                    <React.Fragment key={i}>
                        <span className={classes.date}>{t('activity-date', { date: log.date })}</span>
                        <Radio className={classes.radio} checked disabled={i !== 0} />
                        <StatusIcon
                            className={classes.status}
                            type={enumToStatusType[log.status]}
                            icon={enumToIcon[log.status]}
                        >
                            {t(`common:status.${log.status}`)}
                        </StatusIcon>
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

const useAssetImageClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        gap: 10,
        borderRadius: 5,
        background: theme.colors.neutral[4],
        boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.14)',
    },
    picture: {
        borderRadius: 5,
        maxWidth: 300,
        objectFit: 'cover',
    },
    icon: {
        alignSelf: 'flex-end',
        color: theme.colors.neutral[1],
    },
});

/**
 * This component displays the image of the currently loaded asset.
 */
const AssetImage = () => {
    const classes = useAssetImageClasses();
    const { t } = usePrefixTranslation('asset', { asset: 'drawer.Overview' });
    const { asset } = useTypedSelector(state => ({ asset: state.asset }));
    const [pictureUrl, setPictureUrl] = React.useState('');

    React.useEffect(() => {
        if (!asset.asset || asset.asset.picture === '') return setPictureUrl('');
        getContentUrl(asset.asset.picture).then(url => setPictureUrl(url));
    }, [asset.asset?.picture]);

    if (!asset.asset) return <span>{'#asset not in store'}</span>;

    if (pictureUrl === '') return <></>;

    return (
        <div className={classes.root}>
            <img className={classes.picture} src={pictureUrl} alt='asset' />
            <IconButton className={classes.icon} title={t('download')} onClick={() => downloadFileDialog(pictureUrl)}>
                <Download />
            </IconButton>
        </div>
    );
};

const useLinkCardClasses = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: 10,
        borderRadius: 5,
        background: theme.colors.neutral[4],
        boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.14)',
        color: theme.colors.neutral[1],
        ...theme.typography.content.text,
        marginBottom: 0,
        '& > span': {
            flex: 1,
        },
        '& > a': {
            color: theme.colors.neutral[1],
        },
    },
});

const LinkCard = (props: { label: React.ReactNode; icon: React.ReactNode; href: string }) => {
    const classes = useLinkCardClasses();

    return (
        <div className={classes.root}>
            {props.icon}
            <span>{props.label}</span>
            <a href={props.href} target='_blank' rel='noreferrer'>
                <IconButton size='small'>
                    <ExternalLink />
                </IconButton>
            </a>
        </div>
    );
};
